// ============================================================================
// MÓDULO: INVESTMENT DASHBOARD — "PROJETO MILHÃO"
// ARQUIVO: src/modules/finance/InvestmentDashboard.jsx
// OBJETIVO: Painel de acompanhamento de investimentos pessoais
// ============================================================================

import React, { useState, useMemo, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Area, AreaChart } from 'recharts';
import { subscribeInvestments, addInvestment, updateInvestment, deleteInvestment } from './InvestmentDashboardRepository';
import { FIREBASE_CONFIG } from '../../config/Config';
import { useInvestmentData } from './useInvestmentData';

const appId = FIREBASE_CONFIG.projectId;
const COL_PATH = ['artifacts', appId, 'public', 'data', 'investments'];

// ── CONSTANTES ──────────────────────────────────────────────────────────────
const ASSET_CLASSES = [
  { id: 'RENDA_FIXA', label: 'Renda Fixa', color: '#3B82F6', icon: 'fas fa-shield-alt' },
  { id: 'ACOES', label: 'Ações', color: '#10B981', icon: 'fas fa-chart-line' },
  { id: 'FIIS', label: 'FIIs', color: '#8B5CF6', icon: 'fas fa-building' },
  { id: 'CRIPTO', label: 'Criptomoedas', color: '#F59E0B', icon: 'fab fa-bitcoin' },
  { id: 'TESOURO', label: 'Tesouro Direto', color: '#06B6D4', icon: 'fas fa-landmark' },
  { id: 'EXTERIOR', label: 'Investimento Exterior', color: '#EC4899', icon: 'fas fa-globe-americas' },
  { id: 'PREVIDENCIA', label: 'Previdência Privada', color: '#6366F1', icon: 'fas fa-umbrella' },
  { id: 'OUTROS', label: 'Outros', color: '#78716C', icon: 'fas fa-ellipsis-h' },
];

const ASSET_CLASS_MAP = Object.fromEntries(ASSET_CLASSES.map(a => [a.id, a]));

const STATUS_OPTIONS = [
  { id: 'ACTIVE', label: 'Ativo', color: 'emerald' },
  { id: 'REDEEMED', label: 'Resgatado', color: 'amber' },
  { id: 'MATURED', label: 'Vencido', color: 'gray' },
];

const GOAL_AMOUNT = 1_000_000;

const GOAL_START = new Date('2026-05-19');
const GOAL_DEADLINES = [
  { id: 'REALISTIC', label: 'Meta 10 Anos', years: 10, color: '#20243B', icon: 'fas fa-flag-checkered' },
  { id: 'SURREAL', label: 'Meta 5 Anos', years: 5, color: '#475569', icon: 'fas fa-bolt' },
];

const MONTHLY_CONTRIBUTION = 1500;

const INFLATION_ANNUAL = 0.06;

const INTEREST_SCENARIOS = [
  { id: 'CONSERVATIVE', label: 'Conservador', desc: 'Renda Fixa (CDI/Selic)', annualRate: 0.12, color: '#94A3B8' },
  { id: 'MODERATE', label: 'Moderado', desc: 'Carteira Diversificada', annualRate: 0.14, color: '#475569' },
  { id: 'AGGRESSIVE', label: 'Arrojado', desc: 'Renda Variável Heavy', annualRate: 0.18, color: '#1E293B' },
];

function realRate(nominal, inflation) {
  return (1 + nominal) / (1 + inflation) - 1;
}

function deflate(futureValue, inflationAnnual, years) {
  return futureValue / Math.pow(1 + inflationAnnual, years);
}

const formatBRL = (v) => {
  const n = Number(v) || 0;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatPct = (v) => {
  const n = Number(v) || 0;
  return n.toFixed(2) + '%';
};

const pctOfGoal = (total) => Math.min((total / GOAL_AMOUNT) * 100, 100);

function annualToMonthly(annual) {
  return Math.pow(1 + annual, 1 / 12) - 1;
}

function calcPMT(fv, monthlyRate, months) {
  if (monthlyRate === 0) return fv / months;
  return fv * monthlyRate / (Math.pow(1 + monthlyRate, months) - 1);
}

function calcFV(pmt, monthlyRate, months, pv = 0) {
  if (monthlyRate === 0) return pv + pmt * months;
  const compoundFactor = Math.pow(1 + monthlyRate, months);
  return pv * compoundFactor + pmt * (compoundFactor - 1) / monthlyRate;
}

function calcMonthsToGoal(pmt, monthlyRate, goal, pv = 0) {
  if (pmt <= 0 && pv <= 0) return Infinity;
  if (monthlyRate === 0) return Math.ceil((goal - pv) / pmt);
  let balance = pv;
  let months = 0;
  while (balance < goal && months < 1200) {
    balance = balance * (1 + monthlyRate) + pmt;
    months++;
  }
  return months;
}

function calcDeadlineStats(deadlineYears, currentTotal) {
  const target = new Date(GOAL_START);
  target.setFullYear(target.getFullYear() + deadlineYears);
  const now = new Date();
  const msRemaining = Math.max(target - now, 0);
  const monthsRemaining = Math.max(Math.ceil(msRemaining / (1000 * 60 * 60 * 24 * 30.44)), 1);
  const yearsRemaining = Math.floor(monthsRemaining / 12);
  const monthsRemainder = monthsRemaining % 12;
  const remaining = Math.max(GOAL_AMOUNT - currentTotal, 0);
  const pctElapsed = Math.min(((deadlineYears * 12) - monthsRemaining) / (deadlineYears * 12) * 100, 100);

  const scenarios = INTEREST_SCENARIOS.map(s => {
    const r = annualToMonthly(s.annualRate);
    const pmtNeeded = calcPMT(remaining, r, monthsRemaining);
    const fvWithContrib = calcFV(MONTHLY_CONTRIBUTION, r, monthsRemaining, currentTotal);
    const monthsNeeded = calcMonthsToGoal(MONTHLY_CONTRIBUTION, r, GOAL_AMOUNT, currentTotal);
    return { ...s, pmtNeeded, fvWithContrib, monthsNeeded, monthlyRate: r };
  });

  return { target, monthsRemaining, yearsRemaining, monthsRemainder, remaining, pctElapsed, scenarios };
}

// ── HOOK: useInvestments ────────────────────────────────────────────────────
function useInvestments() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const unsub = subscribeInvestments((list) => {
      setItems(list);
      setLoading(false);
    });
    return unsub;
  }, []);

  const addItem = useCallback(async (data) => {
    return addInvestment(data);
  }, []);

  const updateItem = useCallback(async (id, data) => {
    return updateInvestment(id, data);
  }, []);

  const removeItem = useCallback(async (id) => {
    return deleteInvestment(id);
  }, []);

  return { items, loading, addItem, updateItem, removeItem };
}

// ── MINI COMPONENTS ─────────────────────────────────────────────────────────

function KpiCard({ icon, iconColor, label, value, sub, accent }) {
  return (
    <div className={`rounded-xl border p-5 bg-white shadow-sm ${accent ? 'border-[#C5A059]/40 ring-1 ring-[#C5A059]/20' : 'border-gray-200'}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${iconColor || 'bg-gray-100 text-[#666666]'}`}>
          <i className={`${icon} text-base`} />
        </div>
        <span className="text-xs font-semibold uppercase tracking-wide text-[#666666]">{label}</span>
      </div>
      <div className={`text-2xl font-extrabold ${accent ? 'text-[#C5A059]' : 'text-[#333333]'}`}>{value}</div>
      {sub && <div className="text-sm text-[#666666] mt-1">{sub}</div>}
    </div>
  );
}

function ProgressRing({ pct, size = 140, dark = false }) {
  const strokeW = 14;
  const r = (size - strokeW) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  const trackColor = dark ? 'rgba(255,255,255,0.15)' : '#E5E7EB';
  const textColor = dark ? 'text-white' : 'text-[#333333]';
  const subColor = dark ? 'text-gray-300' : 'text-[#666666]';
  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor} strokeWidth={strokeW} />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#C5A059" strokeWidth={strokeW} strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round" className="transition-all duration-700" style={{ filter: dark ? 'drop-shadow(0 0 6px rgba(197,160,89,0.5))' : 'none' }} />
      </svg>
      <div className="absolute text-center">
        <div className={`text-2xl font-extrabold ${textColor}`}>{pct.toFixed(1)}%</div>
        <div className={`text-xs font-bold ${subColor} uppercase`}>da meta</div>
      </div>
    </div>
  );
}

function AllocationBar({ items }) {
  const total = items.reduce((s, i) => s + (Number(i.currentValue) || 0), 0);
  if (total <= 0) return null;
  const groups = {};
  items.forEach(i => {
    if (i.status === 'REDEEMED' || i.status === 'MATURED') return;
    const cls = i.assetClass || 'OUTROS';
    groups[cls] = (groups[cls] || 0) + (Number(i.currentValue) || 0);
  });
  const sorted = Object.entries(groups).sort((a, b) => b[1] - a[1]);
  const activeTotal = sorted.reduce((s, [, v]) => s + v, 0);

  return (
    <div>
      <div className="flex rounded-full overflow-hidden h-4 bg-gray-100 border border-gray-200">
        {sorted.map(([cls, val]) => {
          const w = (val / activeTotal) * 100;
          const meta = ASSET_CLASS_MAP[cls] || ASSET_CLASS_MAP.OUTROS;
          return (
            <div key={cls} style={{ width: `${w}%`, backgroundColor: meta.color }} className="transition-all duration-500" title={`${meta.label}: ${formatBRL(val)} (${w.toFixed(1)}%)`} />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3">
        {sorted.map(([cls, val]) => {
          const meta = ASSET_CLASS_MAP[cls] || ASSET_CLASS_MAP.OUTROS;
          return (
            <div key={cls} className="flex items-center gap-2 text-sm text-[#333333]">
              <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: meta.color }} />
              <span className="font-semibold">{meta.label}</span>
              <span className="text-[#666666]">{formatPct((val / activeTotal) * 100)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── FORM MODAL ──────────────────────────────────────────────────────────────
function InvestmentFormModal({ item, onSave, onCancel }) {
  const isEdit = Boolean(item?.id);
  const [form, setForm] = useState({
    name: item?.name || '',
    assetClass: item?.assetClass || 'RENDA_FIXA',
    broker: item?.broker || '',
    investedAmount: item?.investedAmount || '',
    currentValue: item?.currentValue || '',
    startDate: item?.startDate || new Date().toISOString().slice(0, 10),
    maturityDate: item?.maturityDate || '',
    yieldRate: item?.yieldRate || '',
    yieldType: item?.yieldType || 'CDI',
    status: item?.status || 'ACTIVE',
    notes: item?.notes || '',
    ticker: item?.ticker || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        investedAmount: Number(form.investedAmount) || 0,
        currentValue: Number(form.currentValue) || Number(form.investedAmount) || 0,
        yieldRate: Number(form.yieldRate) || 0,
      };
      await onSave(payload, item?.id);
    } catch (err) {
      console.error('[Investment] Save error:', err);
      alert('Erro ao salvar: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onCancel} />
      <form onSubmit={handleSubmit} className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
        <div className="bg-[#20243B] px-5 py-3 flex items-center justify-between">
          <h3 className="text-sm font-extrabold text-[#C5A059] uppercase tracking-widest">
            {isEdit ? 'Editar Investimento' : 'Novo Investimento'}
          </h3>
          <button type="button" onClick={onCancel} className="text-gray-400 hover:text-white transition">
            <i className="fas fa-times" />
          </button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Nome */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Nome do Investimento *</label>
            <input value={form.name} onChange={e => set('name', e.target.value)} required placeholder="Ex: CDB Banco Inter 120% CDI" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/30" />
          </div>

          {/* Classe + Corretora */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Classe</label>
              <select value={form.assetClass} onChange={e => set('assetClass', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#C5A059]">
                {ASSET_CLASSES.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Corretora / Banco</label>
              <input value={form.broker} onChange={e => set('broker', e.target.value)} placeholder="Ex: C6 Bank, XP, Inter" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#C5A059]" />
            </div>
          </div>

          {/* Ticker */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Ticker / Código (opcional)</label>
            <input value={form.ticker} onChange={e => set('ticker', e.target.value.toUpperCase())} placeholder="Ex: PETR4, XPML11, BTC" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono outline-none focus:border-[#C5A059]" />
          </div>

          {/* Valores */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Valor Investido (R$)</label>
              <input type="number" step="0.01" min="0" value={form.investedAmount} onChange={e => set('investedAmount', e.target.value)} placeholder="10000.00" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#C5A059]" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Valor Atual (R$)</label>
              <input type="number" step="0.01" min="0" value={form.currentValue} onChange={e => set('currentValue', e.target.value)} placeholder="10500.00" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#C5A059]" />
            </div>
          </div>

          {/* Rendimento */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Taxa / Rendimento (%)</label>
              <input type="number" step="0.01" value={form.yieldRate} onChange={e => set('yieldRate', e.target.value)} placeholder="120" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#C5A059]" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Tipo Rendimento</label>
              <select value={form.yieldType} onChange={e => set('yieldType', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#C5A059]">
                <option value="CDI">% do CDI</option>
                <option value="IPCA_PLUS">IPCA+</option>
                <option value="PREFIXADO">Prefixado</option>
                <option value="SELIC">Selic</option>
                <option value="VARIAVEL">Variável</option>
                <option value="DIVIDENDOS">Dividendos</option>
              </select>
            </div>
          </div>

          {/* Datas */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Data de Aplicação</label>
              <input type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#C5A059]" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Vencimento (opcional)</label>
              <input type="date" value={form.maturityDate} onChange={e => set('maturityDate', e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#C5A059]" />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Status</label>
            <div className="flex gap-2">
              {STATUS_OPTIONS.map(s => (
                <button key={s.id} type="button" onClick={() => set('status', s.id)}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-widest border transition-all ${form.status === s.id ? `bg-${s.color}-100 text-${s.color}-700 border-${s.color}-300` : 'bg-gray-50 text-gray-400 border-gray-200 hover:border-gray-300'}`}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Notas */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 block mb-1">Observações</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2} placeholder="Anotações sobre o investimento..." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:border-[#C5A059] resize-none" />
          </div>
        </div>

        <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel} className="px-4 py-2 text-xs font-bold text-gray-500 border border-gray-300 rounded-lg hover:bg-gray-100 transition">Cancelar</button>
          <button type="submit" disabled={saving} className="px-5 py-2 text-xs font-extrabold uppercase tracking-widest text-white bg-[#C5A059] border border-[#C5A059] rounded-lg hover:bg-[#b8934e] disabled:opacity-50 transition flex items-center gap-2">
            {saving && <i className="fas fa-spinner fa-spin text-[10px]" />}
            {isEdit ? 'Salvar' : 'Criar Investimento'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── INVESTMENT ROW ──────────────────────────────────────────────────────────
function InvestmentRow({ item, onEdit, onDelete }) {
  const meta = ASSET_CLASS_MAP[item.assetClass] || ASSET_CLASS_MAP.OUTROS;
  const invested = Number(item.investedAmount) || 0;
  const current = Number(item.currentValue) || invested;
  const gain = current - invested;
  const gainPct = invested > 0 ? (gain / invested) * 100 : 0;
  const isPositive = gain >= 0;

  return (
    <div className="group flex items-center gap-4 px-4 py-3 border-b border-gray-100 hover:bg-gray-50/80 transition-colors">
      {/* Icon */}
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: meta.color + '18' }}>
        <i className={`${meta.icon} text-sm`} style={{ color: meta.color }} />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-[#20243B] truncate">{item.name}</span>
          {item.ticker && <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0">{item.ticker}</span>}
          {item.status === 'REDEEMED' && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-600 shrink-0">RESGATADO</span>}
          {item.status === 'MATURED' && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 shrink-0">VENCIDO</span>}
        </div>
        <div className="flex items-center gap-3 mt-0.5 text-[10px] text-gray-700">
          <span>{meta.label}</span>
          {item.broker && <><span className="text-gray-300">·</span><span>{item.broker}</span></>}
          {item.yieldRate > 0 && <><span className="text-gray-300">·</span><span>{item.yieldRate}% {item.yieldType}</span></>}
          {item.startDate && <><span className="text-gray-300">·</span><span>{new Date(item.startDate + 'T12:00:00').toLocaleDateString('pt-BR')}</span></>}
        </div>
      </div>

      {/* Values */}
      <div className="text-right shrink-0">
        <div className="text-sm font-extrabold text-[#20243B]">{formatBRL(current)}</div>
        <div className={`text-[11px] font-bold ${isPositive ? 'text-emerald-600' : 'text-red-500'}`}>
          {isPositive ? '+' : ''}{formatBRL(gain)} ({isPositive ? '+' : ''}{gainPct.toFixed(2)}%)
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
        <button type="button" onClick={() => onEdit(item)} className="w-7 h-7 rounded border border-gray-200 bg-white flex items-center justify-center hover:border-[#C5A059] hover:text-[#C5A059] text-gray-400 transition" title="Editar">
          <i className="fas fa-pen text-[9px]" />
        </button>
        <button type="button" onClick={() => onDelete(item)} className="w-7 h-7 rounded border border-gray-200 bg-white flex items-center justify-center hover:border-red-400 hover:text-red-500 text-gray-400 transition" title="Excluir">
          <i className="fas fa-trash text-[9px]" />
        </button>
      </div>
    </div>
  );
}

// ── MAIN DASHBOARD ──────────────────────────────────────────────────────────
export default function InvestmentDashboard() {
  const { items, loading, addItem, updateItem, removeItem } = useInvestments();
  const investmentData = useInvestmentData(items);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [filterClass, setFilterClass] = useState('ALL');
  const [filterStatus, setFilterStatus] = useState('ACTIVE');
  const [searchTerm, setSearchTerm] = useState('');

  const activeItems = useMemo(() => items.filter(i => i.status === 'ACTIVE'), [items]);

  const stats = useMemo(() => {
    const totalInvested = activeItems.reduce((s, i) => s + (Number(i.investedAmount) || 0), 0);
    const firestoreCurrent = activeItems.reduce((s, i) => s + (Number(i.currentValue) || Number(i.investedAmount) || 0), 0);
    // Use Bling CDB balance when available, otherwise Firestore sum
    const totalCurrent = investmentData.blingConnected && investmentData.saldoReservaCDB > 0
      ? investmentData.saldoReservaCDB
      : firestoreCurrent;
    const totalGain = totalCurrent - totalInvested;
    const gainPct = totalInvested > 0 ? (totalGain / totalInvested) * 100 : 0;
    const goalPct = pctOfGoal(totalCurrent);
    const remaining = Math.max(GOAL_AMOUNT - totalCurrent, 0);
    const classCount = new Set(activeItems.map(i => i.assetClass)).size;
    return {
      totalInvested, totalCurrent, totalGain, gainPct, goalPct, remaining, classCount,
      count: activeItems.length, blingConnected: investmentData.blingConnected,
      saldoReservaCDB: investmentData.saldoReservaCDB,
      saldoTotalPortadores: investmentData.saldoTotalPortadores,
    };
  }, [activeItems, investmentData]);

  const filteredItems = useMemo(() => {
    let list = items;
    if (filterStatus !== 'ALL') list = list.filter(i => i.status === filterStatus);
    if (filterClass !== 'ALL') list = list.filter(i => i.assetClass === filterClass);
    if (searchTerm.trim()) {
      const lower = searchTerm.toLowerCase();
      list = list.filter(i =>
        (i.name || '').toLowerCase().includes(lower) ||
        (i.ticker || '').toLowerCase().includes(lower) ||
        (i.broker || '').toLowerCase().includes(lower)
      );
    }
    return list;
  }, [items, filterClass, filterStatus, searchTerm]);

  const handleSave = async (data, existingId) => {
    if (existingId) {
      await updateItem(existingId, data);
    } else {
      await addItem(data);
    }
    setModalOpen(false);
    setEditItem(null);
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Excluir "${item.name}"?`)) return;
    await removeItem(item.id);
  };

  const openNew = () => { setEditItem(null); setModalOpen(true); };
  const openEdit = (item) => { setEditItem(item); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditItem(null); };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-2 border-gray-200 border-t-[#C5A059]" />
          <div className="text-xs font-bold uppercase tracking-wider text-gray-600">Carregando investimentos...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">

      {/* ── HERO BANNER ── */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#20243B] via-[#2a2f4a] to-[#1a1e30] border border-gray-700/50 p-7 md:p-9">
        <div className="absolute top-0 right-0 w-72 h-72 bg-[#C5A059]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-56 h-56 bg-blue-500/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />

        <div className="relative flex flex-col md:flex-row items-start md:items-center gap-8 md:gap-12">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 rounded-xl bg-[#C5A059]/20 border border-[#C5A059]/30 flex items-center justify-center">
                <i className="fas fa-rocket text-[#C5A059] text-xl" />
              </div>
              <div>
                <h1 className="text-3xl font-extrabold text-white tracking-tight">Projeto Milhão</h1>
                <p className="text-sm text-gray-300 font-medium">Sua jornada rumo ao primeiro milhão</p>
                {stats.blingConnected && (
                  <span className="inline-flex items-center gap-1.5 mt-1 px-2 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-[10px] font-bold text-emerald-300 uppercase tracking-wider">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Bling Conectado · CDB: {formatBRL(stats.saldoReservaCDB)}
                  </span>
                )}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-5">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Patrimônio Atual</div>
                <div className="text-2xl font-extrabold text-[#C5A059]">{formatBRL(stats.totalCurrent)}</div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Total Investido</div>
                <div className="text-2xl font-extrabold text-white">{formatBRL(stats.totalInvested)}</div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Rendimento</div>
                <div className={`text-2xl font-extrabold ${stats.totalGain >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {stats.totalGain >= 0 ? '+' : ''}{formatBRL(stats.totalGain)}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Falta p/ Meta</div>
                <div className="text-2xl font-extrabold text-white">{formatBRL(stats.remaining)}</div>
              </div>
            </div>
          </div>

          <div className="shrink-0">
            <ProgressRing pct={stats.goalPct} size={160} dark />
          </div>
        </div>

        {/* Progress bar */}
        <div className="relative mt-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-400">{formatBRL(stats.totalCurrent)}</span>
            <span className="text-sm font-bold text-[#C5A059]">Meta: {formatBRL(GOAL_AMOUNT)}</span>
          </div>
          <div className="h-4 rounded-full bg-white/10 overflow-hidden border border-white/10">
            <div className="h-full rounded-full bg-gradient-to-r from-[#C5A059] to-[#e0c580] transition-all duration-700 relative" style={{ width: `${Math.max(stats.goalPct, 1)}%` }}>
              {stats.goalPct > 3 && <div className="absolute right-2 top-0.5 text-[10px] font-bold text-[#20243B]">{stats.goalPct.toFixed(1)}%</div>}
            </div>
          </div>
          {/* Milestone markers */}
          <div className="relative mt-1 h-4">
            {[25, 50, 75].map(m => (
              <div key={m} className="absolute top-0 flex flex-col items-center" style={{ left: `${m}%`, transform: 'translateX(-50%)' }}>
                <div className="w-px h-2 bg-white/20" />
                <span className="text-[10px] text-gray-500">{m}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── PRAZOS / TIMELINES COM JUROS COMPOSTOS ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {GOAL_DEADLINES.map(d => {
          const dl = calcDeadlineStats(d.years, stats.totalCurrent);
          return (
            <div key={d.id} className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: d.color + '12' }}>
                  <i className={`${d.icon} text-base`} style={{ color: d.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold text-[#333333]">{d.label}</span>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-[#666666]">{d.years} anos</span>
                  </div>
                  <div className="text-sm text-[#666666] mt-0.5">{dl.target.toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}</div>
                </div>
              </div>

              {/* Countdown */}
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-200">
                  <div className="text-2xl font-extrabold text-[#333333]">{dl.yearsRemaining}<span className="text-sm text-[#666666] ml-1">a</span> {dl.monthsRemainder}<span className="text-sm text-[#666666] ml-1">m</span></div>
                  <div className="text-xs font-semibold uppercase text-[#666666] mt-1">tempo restante</div>
                </div>
                <div className="text-center p-3 rounded-lg bg-gray-50 border border-gray-200">
                  <div className="text-xl font-extrabold text-[#333333]">{formatBRL(MONTHLY_CONTRIBUTION)}</div>
                  <div className="text-xs font-semibold uppercase text-[#666666] mt-1">aporte/mês</div>
                </div>
              </div>

              {/* Cenários com juros compostos */}
              <div className="space-y-2 mb-5">
                <div className="text-xs font-bold uppercase text-[#333333] mb-2">
                  <i className="fas fa-chart-line mr-1.5 text-[#C5A059]" />Aporte necessário c/ juros compostos
                </div>
                {dl.scenarios.map(s => (
                  <div key={s.id} className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 bg-gray-50">
                    <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-[#333333]">{s.label}</span>
                        <span className="text-sm font-extrabold text-[#333333]">{formatBRL(s.pmtNeeded)}/mês</span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-xs text-[#666666]">{s.desc} · {(s.annualRate * 100).toFixed(0)}% a.a.</span>
                        <span className="text-xs text-[#999999]">({(s.monthlyRate * 100).toFixed(2)}%/mês)</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Projeção com aporte fixo */}
              <div className="p-4 rounded-lg border border-gray-200 bg-gray-50 mb-4">
                <div className="text-xs font-bold uppercase text-[#333333] mb-3">
                  <i className="fas fa-chart-area mr-1.5 text-[#C5A059]" />Projeção com {formatBRL(MONTHLY_CONTRIBUTION)}/mês em {d.years} anos
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {dl.scenarios.map(s => (
                    <div key={s.id} className="text-center p-2 rounded border border-gray-200 bg-white">
                      <div className="text-base font-extrabold text-[#333333]">{formatBRL(s.fvWithContrib)}</div>
                      <div className="text-xs text-[#666666] mt-0.5">{s.label}</div>
                      <div className={`text-xs font-bold mt-0.5 ${s.fvWithContrib >= GOAL_AMOUNT ? 'text-green-600' : 'text-[#999999]'}`}>
                        {s.fvWithContrib >= GOAL_AMOUNT ? '✓ META' : `${((s.fvWithContrib / GOAL_AMOUNT) * 100).toFixed(0)}%`}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Time elapsed bar */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold text-[#666666]">Tempo decorrido</span>
                  <span className="text-xs font-bold text-[#333333]">{dl.pctElapsed.toFixed(1)}%</span>
                </div>
                <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-700" style={{ width: `${dl.pctElapsed}%`, backgroundColor: d.color }} />
                </div>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-xs text-[#666666]">Mai/2026</span>
                  <span className="text-xs font-semibold text-[#333333]">{dl.target.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' })}</span>
                </div>
              </div>

              {/* Tempo real p/ meta com aporte planejado */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-xs font-bold uppercase text-[#333333] mb-2">
                  <i className="fas fa-stopwatch mr-1.5 text-[#C5A059]" />Tempo real p/ R$ 1M com {formatBRL(MONTHLY_CONTRIBUTION)}/mês
                </div>
                <div className="flex items-center gap-2">
                  {dl.scenarios.map(s => {
                    const y = Math.floor(s.monthsNeeded / 12);
                    const m = s.monthsNeeded % 12;
                    return (
                      <div key={s.id} className="flex-1 text-center p-2 rounded border border-gray-200 bg-white">
                        <div className="text-base font-extrabold text-[#333333]">{y}a {m}m</div>
                        <div className="text-xs text-[#666666]">{s.label}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── KPI CARDS ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon="fas fa-wallet" iconColor="bg-blue-50 text-blue-600" label="Ativos" value={stats.count} sub={`${stats.classCount} classe${stats.classCount !== 1 ? 's' : ''}`} />
        <KpiCard icon="fas fa-percentage" iconColor="bg-emerald-50 text-emerald-600" label="Rentabilidade" value={formatPct(stats.gainPct)} sub="desde o início" />
        <KpiCard icon="fas fa-rocket" iconColor="bg-[#C5A059]/10 text-[#C5A059]" label="Progresso" value={formatPct(stats.goalPct)} sub="do Projeto Milhão" accent />
        <KpiCard icon="fas fa-bullseye" iconColor="bg-purple-50 text-purple-600" label="Meta" value={formatBRL(GOAL_AMOUNT)} sub="objetivo final" />
      </div>

      {/* ── GRÁFICO DE PROJEÇÃO (Tempo × Dinheiro) ── */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-sm font-bold text-[#333333] mb-1">
          <i className="fas fa-chart-area mr-2 text-[#C5A059]" />Evolução Patrimonial Projetada
        </h3>
        <p className="text-sm text-[#666666] mb-5">
          Crescimento do patrimônio ao longo de 10 anos com aporte de {formatBRL(MONTHLY_CONTRIBUTION)}/mês em cada cenário de juros.
        </p>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={(() => {
              const data = [];
              for (let year = 0; year <= 10; year++) {
                const months = year * 12;
                const row = { ano: year, label: `Ano ${year}` };
                INTEREST_SCENARIOS.forEach(s => {
                  const r = annualToMonthly(s.annualRate);
                  row[s.id] = Math.round(calcFV(MONTHLY_CONTRIBUTION, r, months, stats.totalCurrent));
                  row[`${s.id}_real`] = Math.round(deflate(row[s.id], INFLATION_ANNUAL, year));
                });
                data.push(row);
              }
              return data;
            })()} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
              <defs>
                <linearGradient id="gradConservador" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#94A3B8" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#94A3B8" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradModerado" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#475569" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#475569" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradArrojado" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#C5A059" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#C5A059" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="ano" tick={{ fontSize: 12, fill: '#666666' }} label={{ value: 'Anos', position: 'insideBottom', offset: -3, style: { fontSize: 12, fill: '#666666' } }} />
              <YAxis tick={{ fontSize: 11, fill: '#666666' }} tickFormatter={(v) => `R$ ${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => formatBRL(value)} labelFormatter={(v) => `Ano ${v}`} contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13 }} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <ReferenceLine y={GOAL_AMOUNT} stroke="#C5A059" strokeDasharray="5 5" strokeWidth={2} label={{ value: 'META R$ 1M', position: 'insideTopRight', style: { fontSize: 11, fill: '#C5A059', fontWeight: 700 } }} />
              <Area type="monotone" dataKey="CONSERVATIVE" name="Conservador (12% a.a.)" stroke="#94A3B8" strokeWidth={2} fill="url(#gradConservador)" />
              <Area type="monotone" dataKey="MODERATE" name="Moderado (14% a.a.)" stroke="#475569" strokeWidth={2} fill="url(#gradModerado)" />
              <Area type="monotone" dataKey="AGGRESSIVE" name="Arrojado (18% a.a.)" stroke="#C5A059" strokeWidth={2.5} fill="url(#gradArrojado)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 text-xs text-[#999999] text-center">
          Linha tracejada dourada = meta de R$ 1.000.000. Valores nominais (sem descontar inflação). Patrimônio inicial: {formatBRL(stats.totalCurrent)}.
        </div>
      </div>

      {/* ── GRÁFICO: SEU DINHEIRO vs JUROS COMPOSTOS ── */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-sm font-bold text-[#333333] mb-1">
          <i className="fas fa-magic mr-2 text-[#C5A059]" />O Poder dos Juros Compostos (cenário Moderado 14% a.a.)
        </h3>
        <p className="text-sm text-[#666666] mb-5">
          Quanto do patrimônio final vem dos seus aportes vs. quanto vem dos juros trabalhando por você.
        </p>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={(() => {
              const data = [];
              const r = annualToMonthly(0.14);
              for (let year = 0; year <= 10; year++) {
                const months = year * 12;
                const totalDeposited = stats.totalCurrent + MONTHLY_CONTRIBUTION * months;
                const totalWithInterest = Math.round(calcFV(MONTHLY_CONTRIBUTION, r, months, stats.totalCurrent));
                const interestEarned = Math.max(0, totalWithInterest - totalDeposited);
                data.push({ ano: year, aportes: Math.round(totalDeposited), juros: Math.round(interestEarned), total: totalWithInterest });
              }
              return data;
            })()} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
              <defs>
                <linearGradient id="gradAportes" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#475569" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#475569" stopOpacity={0.1} />
                </linearGradient>
                <linearGradient id="gradJuros" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#C5A059" stopOpacity={0.6} />
                  <stop offset="95%" stopColor="#C5A059" stopOpacity={0.1} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="ano" tick={{ fontSize: 12, fill: '#666666' }} label={{ value: 'Anos', position: 'insideBottom', offset: -3, style: { fontSize: 12, fill: '#666666' } }} />
              <YAxis tick={{ fontSize: 11, fill: '#666666' }} tickFormatter={(v) => `R$ ${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => formatBRL(value)} labelFormatter={(v) => `Ano ${v}`} contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13 }} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Area type="monotone" dataKey="aportes" name="Seus Aportes" stroke="#475569" strokeWidth={2} fill="url(#gradAportes)" stackId="1" />
              <Area type="monotone" dataKey="juros" name="Juros Compostos" stroke="#C5A059" strokeWidth={2} fill="url(#gradJuros)" stackId="1" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {/* Summary cards */}
        {(() => {
          const r = annualToMonthly(0.14);
          const totalDeposited10y = stats.totalCurrent + MONTHLY_CONTRIBUTION * 120;
          const totalWithInterest10y = Math.round(calcFV(MONTHLY_CONTRIBUTION, r, 120, stats.totalCurrent));
          const interestEarned10y = totalWithInterest10y - totalDeposited10y;
          const interestPct = totalWithInterest10y > 0 ? ((interestEarned10y / totalWithInterest10y) * 100).toFixed(0) : 0;
          return (
            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 text-center">
                <div className="text-xs text-[#666666] mb-1">Seus Aportes (10a)</div>
                <div className="text-lg font-extrabold text-[#333333]">{formatBRL(totalDeposited10y)}</div>
              </div>
              <div className="p-3 rounded-lg bg-[#C5A059]/5 border border-[#C5A059]/20 text-center">
                <div className="text-xs text-[#666666] mb-1">Juros Compostos (10a)</div>
                <div className="text-lg font-extrabold text-[#C5A059]">{formatBRL(interestEarned10y)}</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 text-center">
                <div className="text-xs text-[#666666] mb-1">% que veio dos Juros</div>
                <div className="text-lg font-extrabold text-[#333333]">{interestPct}%</div>
              </div>
            </div>
          );
        })()}
        <div className="mt-3 text-xs text-[#999999] text-center">
          A partir do ano 6-7, os juros compostos geram MAIS dinheiro que seus aportes mensais. Esse é o "efeito bola de neve".
        </div>
      </div>

      {/* ── CUSTO DA PROCRASTINAÇÃO ── */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-sm font-bold text-[#333333] mb-1">
          <i className="fas fa-hourglass-half mr-2 text-red-500" />Custo da Procrastinação
        </h3>
        <p className="text-sm text-[#666666] mb-5">
          Quanto você perde no patrimônio final (em 10 anos) para cada mês que atrasa o início dos aportes.
        </p>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={(() => {
              const r = annualToMonthly(0.14);
              const baseline = calcFV(MONTHLY_CONTRIBUTION, r, 120, stats.totalCurrent);
              const data = [];
              for (let delay = 0; delay <= 24; delay += 3) {
                const remainingMonths = 120 - delay;
                const fv = remainingMonths > 0 ? calcFV(MONTHLY_CONTRIBUTION, r, remainingMonths, stats.totalCurrent) : stats.totalCurrent;
                const lost = baseline - fv;
                data.push({ meses: delay, patrimonio: Math.round(fv), perdido: Math.round(lost) });
              }
              return data;
            })()} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="meses" tick={{ fontSize: 12, fill: '#666666' }} label={{ value: 'Meses de atraso', position: 'insideBottom', offset: -3, style: { fontSize: 12, fill: '#666666' } }} />
              <YAxis tick={{ fontSize: 11, fill: '#666666' }} tickFormatter={(v) => `R$ ${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => formatBRL(value)} labelFormatter={(v) => `${v} meses de atraso`} contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13 }} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Area type="monotone" dataKey="patrimonio" name="Patrimônio Final" stroke="#475569" strokeWidth={2} fill="#475569" fillOpacity={0.15} />
              <Area type="monotone" dataKey="perdido" name="Dinheiro Perdido" stroke="#DC2626" strokeWidth={2} fill="#DC2626" fillOpacity={0.1} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {(() => {
          const r = annualToMonthly(0.14);
          const baseline = calcFV(MONTHLY_CONTRIBUTION, r, 120, stats.totalCurrent);
          const delay6m = calcFV(MONTHLY_CONTRIBUTION, r, 114, stats.totalCurrent);
          const delay12m = calcFV(MONTHLY_CONTRIBUTION, r, 108, stats.totalCurrent);
          return (
            <div className="mt-4 grid grid-cols-3 gap-3">
              <div className="p-3 rounded-lg bg-red-50 border border-red-100 text-center">
                <div className="text-xs text-[#666666] mb-1">Custo de 6 meses</div>
                <div className="text-lg font-extrabold text-red-600">-{formatBRL(baseline - delay6m)}</div>
              </div>
              <div className="p-3 rounded-lg bg-red-50 border border-red-100 text-center">
                <div className="text-xs text-[#666666] mb-1">Custo de 12 meses</div>
                <div className="text-lg font-extrabold text-red-600">-{formatBRL(baseline - delay12m)}</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 text-center">
                <div className="text-xs text-[#666666] mb-1">Custo por mês</div>
                <div className="text-lg font-extrabold text-[#333333]">~{formatBRL((baseline - delay12m) / 12)}</div>
              </div>
            </div>
          );
        })()}
        <div className="mt-3 text-xs text-[#999999] text-center">
          Cada mês sem investir não custa apenas R$ 1.500 — custa os juros compostos que aquele aporte geraria pelos próximos 10 anos.
        </div>
      </div>

      {/* ── RETROSPECTIVA: NOMINAL vs REAL NOS ÚLTIMOS 10 ANOS ── */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-sm font-bold text-[#333333] mb-1">
          <i className="fas fa-history mr-2 text-[#C5A059]" />Retrospectiva — E se você tivesse investido nos últimos 10 anos?
        </h3>
        <p className="text-sm text-[#666666] mb-5">
          Simulação: R$ {(MONTHLY_CONTRIBUTION).toLocaleString('pt-BR')}/mês a 14% a.a. de mai/2016 a mai/2026. Comparação entre valor nominal acumulado e valor real (corrigido pela inflação de {(INFLATION_ANNUAL * 100).toFixed(0)}% a.a.).
        </p>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={(() => {
              const data = [];
              const r = annualToMonthly(0.14);
              for (let year = 0; year <= 10; year++) {
                const months = year * 12;
                const nominalFV = Math.round(calcFV(MONTHLY_CONTRIBUTION, r, months, 0));
                const totalDeposited = MONTHLY_CONTRIBUTION * months;
                const yearsFromEnd = 10 - year;
                const realFV = Math.round(deflate(nominalFV, INFLATION_ANNUAL, yearsFromEnd));
                data.push({
                  ano: 2016 + year,
                  nominal: nominalFV,
                  real: realFV,
                  depositado: totalDeposited,
                });
              }
              return data;
            })()} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
              <defs>
                <linearGradient id="gradNominalHist" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#475569" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#475569" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradRealHist" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#C5A059" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#C5A059" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="ano" tick={{ fontSize: 12, fill: '#666666' }} />
              <YAxis tick={{ fontSize: 11, fill: '#666666' }} tickFormatter={(v) => `R$ ${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => formatBRL(value)} contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13 }} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Area type="monotone" dataKey="depositado" name="Aportes Puros (sem juros)" stroke="#94A3B8" strokeWidth={1.5} strokeDasharray="4 4" fill="none" />
              <Area type="monotone" dataKey="nominal" name="Valor Nominal (com juros)" stroke="#475569" strokeWidth={2.5} fill="url(#gradNominalHist)" />
              <Area type="monotone" dataKey="real" name="Valor Real (poder de compra hoje)" stroke="#C5A059" strokeWidth={2.5} fill="url(#gradRealHist)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {/* Summary: final numbers */}
        {(() => {
          const r = annualToMonthly(0.14);
          const nominalFinal = Math.round(calcFV(MONTHLY_CONTRIBUTION, r, 120, 0));
          const totalDeposited = MONTHLY_CONTRIBUTION * 120;
          const realFinal = Math.round(nominalFinal);
          const inflationErosion = nominalFinal - deflate(nominalFinal, INFLATION_ANNUAL, 10);
          const jurosGanhos = nominalFinal - totalDeposited;
          return (
            <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 text-center">
                <div className="text-xs text-[#666666] mb-1">Depositado (10a)</div>
                <div className="text-lg font-extrabold text-[#333333]">{formatBRL(totalDeposited)}</div>
              </div>
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-200 text-center">
                <div className="text-xs text-[#666666] mb-1">Valor Nominal</div>
                <div className="text-lg font-extrabold text-[#333333]">{formatBRL(nominalFinal)}</div>
              </div>
              <div className="p-3 rounded-lg bg-[#C5A059]/5 border border-[#C5A059]/20 text-center">
                <div className="text-xs text-[#666666] mb-1">Valor Real (hoje)</div>
                <div className="text-lg font-extrabold text-[#C5A059]">{formatBRL(deflate(nominalFinal, INFLATION_ANNUAL, 10))}</div>
              </div>
              <div className="p-3 rounded-lg bg-red-50 border border-red-100 text-center">
                <div className="text-xs text-[#666666] mb-1">Corroído pela Inflação</div>
                <div className="text-lg font-extrabold text-red-600">-{formatBRL(inflationErosion)}</div>
              </div>
            </div>
          );
        })()}
        <div className="mt-4 text-xs text-[#999999] text-center">
          A diferença entre as linhas Nominal e Real mostra quanto a inflação "comeu" do seu patrimônio. Mesmo assim, investir a 14% a.a. gerou retorno real significativo (acima da inflação).
        </div>

        {/* Tabela comparativa ano a ano */}
        <details className="mt-5">
          <summary className="text-sm font-bold text-[#333333] cursor-pointer hover:text-[#C5A059] transition-colors">
            <i className="fas fa-table mr-2 text-[#C5A059]" />Ver tabela detalhada ano a ano
          </summary>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 text-xs font-bold text-[#666666]">Ano</th>
                  <th className="text-right py-2 px-3 text-xs font-bold text-[#666666]">Depositado</th>
                  <th className="text-right py-2 px-3 text-xs font-bold text-[#666666]">Nominal</th>
                  <th className="text-right py-2 px-3 text-xs font-bold text-[#C5A059]">Real (hoje)</th>
                  <th className="text-right py-2 px-3 text-xs font-bold text-red-500">Perda Inflação</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 11 }, (_, i) => i).map(year => {
                  const months = year * 12;
                  const r = annualToMonthly(0.14);
                  const nominalFV = calcFV(MONTHLY_CONTRIBUTION, r, months, 0);
                  const deposited = MONTHLY_CONTRIBUTION * months;
                  const yearsFromEnd = 10 - year;
                  const realFV = deflate(nominalFV, INFLATION_ANNUAL, yearsFromEnd);
                  const loss = nominalFV - realFV;
                  return (
                    <tr key={year} className={`border-b border-gray-100 ${year === 10 ? 'bg-[#C5A059]/5 font-bold' : ''}`}>
                      <td className="py-2 px-3 text-[#333333]">{2016 + year}</td>
                      <td className="py-2 px-3 text-right text-[#666666]">{formatBRL(deposited)}</td>
                      <td className="py-2 px-3 text-right text-[#333333]">{formatBRL(nominalFV)}</td>
                      <td className="py-2 px-3 text-right text-[#C5A059]">{formatBRL(realFV)}</td>
                      <td className="py-2 px-3 text-right text-red-500">{year > 0 ? `-${formatBRL(loss)}` : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </details>
      </div>

      {/* ── PROJEÇÃO 10 ANOS (simulação, não meta) ── */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-sm font-bold text-[#333333] mb-1">
          <i className="fas fa-binoculars mr-2 text-[#C5A059]" />Simulador de Projeção — 10 Anos no Futuro
        </h3>
        <p className="text-sm text-[#666666] mb-5">Onde estará seu patrimônio em maio/2036 considerando juros compostos + aportes mensais constantes?</p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-3 text-xs font-bold uppercase text-[#666666]">Aporte/mês</th>
                {INTEREST_SCENARIOS.map(s => (
                  <th key={s.id} className="text-center py-3 px-3">
                    <div className="text-xs font-bold" style={{ color: s.color }}>{s.label}</div>
                    <div className="text-xs text-[#999999]">{(s.annualRate * 100).toFixed(0)}% a.a.</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[1000, 1500, 2000, 3000, 5000].map(pmt => (
                <tr key={pmt} className={`border-b border-gray-100 ${pmt === MONTHLY_CONTRIBUTION ? 'bg-[#C5A059]/5' : ''}`}>
                  <td className="py-3 px-3">
                    <span className={`font-bold ${pmt === MONTHLY_CONTRIBUTION ? 'text-[#C5A059]' : 'text-[#333333]'}`}>
                      {formatBRL(pmt)}
                    </span>
                    {pmt === MONTHLY_CONTRIBUTION && <span className="ml-2 text-xs text-[#C5A059] font-semibold">← ATUAL</span>}
                  </td>
                  {INTEREST_SCENARIOS.map(s => {
                    const r = annualToMonthly(s.annualRate);
                    const fv = calcFV(pmt, r, 120, stats.totalCurrent);
                    const isGoal = fv >= GOAL_AMOUNT;
                    return (
                      <td key={s.id} className="text-center py-3 px-3">
                        <div className={`font-extrabold ${isGoal ? 'text-green-600' : 'text-[#333333]'}`}>{formatBRL(fv)}</div>
                        <div className="text-xs text-[#999999]">
                          {isGoal ? '✓ MILHÃO' : `${((fv / GOAL_AMOUNT) * 100).toFixed(0)}% da meta`}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-4 pt-4 border-t border-gray-200 text-sm text-[#666666]">
          <i className="fas fa-info-circle mr-1.5 text-[#C5A059]" />
          Projeção simplificada (taxa constante, sem inflação, sem IR). Patrimônio inicial: {formatBRL(stats.totalCurrent)}. Período: 120 meses.
        </div>
      </div>

      {/* ── PODER DE COMPRA REAL (inflação) ── */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h3 className="text-sm font-bold text-[#333333] mb-1">
          <i className="fas fa-balance-scale mr-2 text-[#C5A059]" />Poder de Compra Real (Inflação {(INFLATION_ANNUAL * 100).toFixed(0)}% a.a.)
        </h3>
        <p className="text-sm text-[#666666] mb-5">
          O dinheiro perde valor com o tempo. Aqui mostramos quanto R$ 1.000.000 realmente vale no futuro e qual é o retorno REAL dos seus investimentos.
        </p>

        {/* Bloco 1: O que R$1M compra no futuro */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
          {[3, 5, 10].map(y => {
            const realValue = deflate(GOAL_AMOUNT, INFLATION_ANNUAL, y);
            const loss = GOAL_AMOUNT - realValue;
            return (
              <div key={y} className="p-4 rounded-lg border border-gray-200 bg-gray-50 text-center">
                <div className="text-xs font-semibold uppercase text-[#666666] mb-2">R$ 1M em {y} anos</div>
                <div className="text-2xl font-extrabold text-[#333333]">{formatBRL(realValue)}</div>
                <div className="text-sm text-[#666666] mt-1">em poder de compra de hoje</div>
                <div className="text-sm font-semibold text-[#C5A059] mt-2">Perda: {formatBRL(loss)} ({((loss / GOAL_AMOUNT) * 100).toFixed(0)}%)</div>
              </div>
            );
          })}
        </div>

        {/* Bloco 2: Retorno REAL (descontada inflação) */}
        <div className="mb-5">
          <div className="text-xs font-bold uppercase text-[#333333] mb-3">
            <i className="fas fa-chart-bar mr-1.5 text-[#C5A059]" />Retorno Real vs Nominal (Equação de Fisher)
          </div>
          <div className="grid grid-cols-3 gap-3">
            {INTEREST_SCENARIOS.map(s => {
              const rReal = realRate(s.annualRate, INFLATION_ANNUAL);
              return (
                <div key={s.id} className="p-4 rounded-lg border border-gray-200 bg-gray-50 text-center">
                  <div className="text-sm font-bold text-[#333333] mb-2">{s.label}</div>
                  <div className="flex items-center justify-center gap-3">
                    <div>
                      <div className="text-lg font-extrabold text-[#333333]">{(s.annualRate * 100).toFixed(0)}%</div>
                      <div className="text-xs text-[#999999]">nominal</div>
                    </div>
                    <span className="text-[#999999] text-lg">→</span>
                    <div>
                      <div className="text-lg font-extrabold text-[#333333]">{(rReal * 100).toFixed(2)}%</div>
                      <div className="text-xs text-[#999999]">real</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bloco 3: Projeção REAL (valor de hoje) com R$1500/mês */}
        <div className="mb-4">
          <div className="text-xs font-bold uppercase text-[#333333] mb-3">
            <i className="fas fa-coins mr-1.5 text-[#C5A059]" />Quanto seu patrimônio REALMENTE valerá (em reais de hoje) com {formatBRL(MONTHLY_CONTRIBUTION)}/mês
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-3 text-xs font-bold text-[#666666]">Prazo</th>
                  {INTEREST_SCENARIOS.map(s => (
                    <th key={s.id} className="text-center py-3 px-3 text-xs font-bold" style={{ color: s.color }}>{s.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[3, 5, 7, 10].map(y => (
                  <tr key={y} className="border-b border-gray-100">
                    <td className="py-3 px-3 font-bold text-[#333333]">{y} anos</td>
                    {INTEREST_SCENARIOS.map(s => {
                      const r = annualToMonthly(s.annualRate);
                      const nominalFV = calcFV(MONTHLY_CONTRIBUTION, r, y * 12, stats.totalCurrent);
                      const realFV = deflate(nominalFV, INFLATION_ANNUAL, y);
                      return (
                        <td key={s.id} className="text-center py-3 px-3">
                          <div className="font-extrabold text-[#333333]">{formatBRL(realFV)}</div>
                          <div className="text-xs text-[#999999]">nominal: {formatBRL(nominalFV)}</div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Nota de rodapé */}
        <div className="pt-4 border-t border-gray-200 text-sm text-[#666666]">
          <i className="fas fa-info-circle mr-1.5 text-[#C5A059]" />
          IPCA estimado: {(INFLATION_ANNUAL * 100).toFixed(0)}% a.a. (média histórica Brasil). Retorno real = (1 + nominal) ÷ (1 + inflação) − 1. Valores em "reais de hoje" mostram o poder de compra equivalente.
        </div>
      </div>

      {/* ── ALLOCATION ── */}
      {activeItems.length > 0 && (
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
          <h3 className="text-sm font-bold text-[#333333] mb-4">
            <i className="fas fa-chart-pie mr-2 text-[#C5A059]" />Alocação por Classe de Ativo
          </h3>
          <AllocationBar items={activeItems} />
        </div>
      )}

      {/* ── LISTA DE INVESTIMENTOS ── */}
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {/* Toolbar */}
        <div className="px-5 py-4 border-b border-gray-200 flex flex-wrap items-center gap-3 bg-gray-50">
          <h3 className="text-sm font-bold text-[#333333] mr-auto">
            <i className="fas fa-list mr-2 text-[#C5A059]" />Carteira ({filteredItems.length})
          </h3>

          <div className="relative">
            <i className="fas fa-search absolute left-3 top-2.5 text-[#999999] text-xs" />
            <input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Buscar..."
              className="pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-[#C5A059] w-48"
            />
          </div>

          <select
            value={filterClass}
            onChange={e => setFilterClass(e.target.value)}
            className="py-2 px-3 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-[#C5A059]"
          >
            <option value="ALL">Todas as Classes</option>
            {ASSET_CLASSES.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
          </select>

          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="py-2 px-3 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-[#C5A059]"
          >
            <option value="ALL">Todos</option>
            {STATUS_OPTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>

          <button type="button" onClick={openNew} className="inline-flex items-center gap-2 rounded-lg border border-[#C5A059] bg-[#C5A059] px-5 py-2 text-xs font-bold uppercase text-[#20243B] hover:bg-[#b8934e] transition shadow-sm">
            <i className="fas fa-plus" /> Novo
          </button>
        </div>

        {/* Items */}
        {filteredItems.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-100 flex items-center justify-center">
              <i className="fas fa-chart-line text-2xl text-[#999999]" />
            </div>
            <div className="text-base font-bold text-[#666666]">Nenhum investimento encontrado</div>
            <div className="text-sm text-[#999999] mt-1">Clique em "+ Novo" para começar sua carteira</div>
            <button type="button" onClick={openNew} className="mt-4 inline-flex items-center gap-2 rounded-lg bg-[#C5A059] px-5 py-2.5 text-sm font-bold uppercase text-[#20243B] hover:bg-[#b8934e] transition">
              <i className="fas fa-plus" /> Criar Primeiro Investimento
            </button>
          </div>
        ) : (
          <div>
            {filteredItems.map(item => (
              <InvestmentRow key={item.id} item={item} onEdit={openEdit} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </div>

      {/* ── DICA EDUCACIONAL — Investidor Sardinha ── */}
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-lg bg-[#20243B]/10 flex items-center justify-center shrink-0">
            <i className="fas fa-graduation-cap text-[#20243B] text-lg" />
          </div>
          <div>
            <h4 className="text-sm font-bold text-[#333333]">Metodologia Investidor Sardinha (Raul Sena)</h4>
            <p className="text-sm text-[#666666] mt-2 leading-relaxed">
              <strong>Juros compostos são o motor.</strong> R$ 1.500/mês a 14% a.a. vira ~R$ 360 mil em 10 anos — dos quais ~R$ 180 mil são juros puros.
              Estratégia AUVP: <strong>reserva de emergência primeiro</strong> (6-12 meses em Tesouro Selic/CDB 100% CDI), depois diversificar entre renda fixa (Tesouro IPCA+, CDBs), FIIs e ações.
              O segredo não é acertar o timing — é <strong>consistência nos aportes</strong> e reinvestir os dividendos. Cada mês sem aporte é um mês que os juros compostos perdem força.
            </p>
            <p className="text-xs text-[#999999] mt-2 italic">
              Fonte: Investidor Sardinha · youtube.com/@investidorsardinha · Calculadora do Primeiro Milhão
            </p>
          </div>
        </div>
      </div>

      {/* ── MODAL ── */}
      {modalOpen && (
        <InvestmentFormModal
          item={editItem}
          onSave={handleSave}
          onCancel={closeModal}
        />
      )}
    </div>
  );
}
